import React, { useState } from 'react';
import { X, Search, Copy, AlertCircle, CheckCircle2, Loader2, CreditCard, Calendar, DollarSign, Hash, Info, User, MapPin, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import { createPortal } from 'react-dom';
import { supabase } from '../../src/lib/supabase';
import { useToast } from '../../src/contexts/ToastContext';
import { propsFundoModal } from '../../src/lib/backdropModal';
import type { ContaSicoob } from '../../types';

interface ConsultarCobrancaModalProps {
    isOpen: boolean;
    onClose: () => void;
}

/** Etiqueta da conta no resultado. Conta fora da lista cai no cinza. */
const COR_ETIQUETA_CONTA: Record<string, string> = {
    associacao: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-900/20 dark:text-teal-300 dark:border-teal-800',
    gestao: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-300 dark:border-indigo-800',
    hsolar: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-300 dark:border-amber-800',
};
const COR_ETIQUETA_OUTRA = 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700';

interface SicoobBoleto {
    /** Conta Sicoob de onde este boleto veio — o n8n consulta todas e etiqueta cada
     * resultado. As contas têm sequências próprias de nossoNumero, então o mesmo
     * número existe em mais de uma apontando para boletos de clientes diferentes. */
    conta?: ContaSicoob;
    conta_nome?: string;
    nossoNumero?: number;
    seuNumero?: string;
    identificacaoBoletoEmpresa?: string;
    codigoBarras?: string;
    linhaDigitavel?: string;
    valor?: number;
    dataEmissao?: string;
    dataVencimento?: string;
    dataLimitePagamento?: string;
    dataLiquidacao?: string;
    dataMovimentoLiquidacao?: string;
    dataPagamento?: string;
    dataBaixa?: string;
    dataCredito?: string;
    valorAbatimento?: number;
    codigoModalidade?: number;
    numeroContaCorrente?: number;
    numeroCliente?: number;
    codigoEspecieDocumento?: string;
    situacao?: string;
    situacaoBoleto?: string; // campo real retornado pela API
    pagador?: {
        nome?: string;
        numeroCpfCnpj?: string;
        endereco?: string;
        bairro?: string;
        cidade?: string;
        uf?: string;
        cep?: string;
    };
    listaHistorico?: Array<{
        tipoHistorico?: number | string; // a API retorna como string
        dataHistorico?: string;          // campo real da API
        data?: string;                   // fallback
        descricaoHistorico?: string;     // campo real da API
        descricao?: string;              // fallback
    }>;
}

const tipoHistoricoLabel: Record<string, string> = {
    '1': 'Em aberto',
    '2': 'Alteração',
    '3': 'Prorrogação',
    '4': 'Tarifas',
    '5': 'Protesto',
    '6': 'Liquidação / Baixa',
};

type SearchMode = 'individual' | 'cpf';

const ConsultarCobrancaModal: React.FC<ConsultarCobrancaModalProps> = ({ isOpen, onClose }) => {
    const { addToast } = useToast();
    const [searchMode, setSearchMode] = useState<SearchMode>('individual');
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState<SicoobBoleto | null>(null);
    const [resultadoLista, setResultadoLista] = useState<SicoobBoleto[]>([]);
    /** Mesmo nossoNumero achado nas duas contas Sicoob — quem consultou precisa escolher. */
    const [ambiguo, setAmbiguo] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

    // Individual search fields
    const [nossoNumero, setNossoNumero] = useState('');
    const [linhaDigitavel, setLinhaDigitavel] = useState('');

    // CPF search fields
    const [cpfCnpj, setCpfCnpj] = useState('');
    const [dataInicio, setDataInicio] = useState('');
    const [dataFim, setDataFim] = useState('');

    const handleCopy = (text: string, label: string) => {
        navigator.clipboard.writeText(text);
        addToast(`${label} copiado!`, 'success');
    };

    const getWebhookUrl = async (key: string): Promise<string | null> => {
        const { data } = await supabase
            .from('system_settings')
            .select('value')
            .eq('key', key)
            .single();
        return data?.value || null;
    };

    const handleSearch = async () => {
        setLoading(true);
        setErro(null);
        setResultado(null);
        setResultadoLista([]);
        setAmbiguo(false);
        setExpandedIdx(null);

        try {
            if (searchMode === 'individual') {
                if (!nossoNumero.trim() && !linhaDigitavel.trim()) {
                    setErro('Informe ao menos um critério: Nosso Número ou Linha Digitável.');
                    return;
                }

                const webhookUrl = await getWebhookUrl('sicoob_consulta_webhook_url');
                if (!webhookUrl) {
                    setErro('Webhook de consulta Sicoob não configurado. Acesse Configurações → APIs e Webhooks.');
                    return;
                }

                const payload: Record<string, any> = {};
                if (nossoNumero.trim()) payload.nossoNumero = Number(nossoNumero.trim());
                if (linhaDigitavel.trim()) payload.linhaDigitavel = linhaDigitavel.trim();

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    let msg = `Erro ${response.status} ao consultar o Sicoob.`;
                    try { const e = await response.json(); msg = e?.mensagem || e?.message || e?.error || msg; } catch (_) {}
                    throw new Error(msg);
                }

                const data = await response.json();

                // O fluxo consulta as DUAS contas e devolve `resultados` etiquetado.
                // Quando o mesmo nossoNumero existe nas duas, vêm dois boletos de
                // clientes diferentes — mostrar os dois e deixar a escolha com quem
                // consultou, em vez de eleger um em silêncio.
                const lista: SicoobBoleto[] = Array.isArray(data?.resultados)
                    ? data.resultados
                    : (data?.resultado ? [data.resultado] : (data?.nossoNumero ? [data] : []));

                if (lista.length === 0) {
                    setErro('Nenhum boleto encontrado com os critérios informados.');
                    return;
                }

                if (lista.length === 1) {
                    setResultado(lista[0]);
                } else {
                    setAmbiguo(true);
                    setResultadoLista(lista);
                }

            } else {
                // CPF/CNPJ search
                const cpfLimpo = cpfCnpj.replace(/\D/g, '');
                if (!cpfLimpo) {
                    setErro('Informe o CPF ou CNPJ do pagador.');
                    return;
                }

                const webhookUrl = await getWebhookUrl('sicoob_consulta_pagador_webhook_url');
                if (!webhookUrl) {
                    setErro('Webhook de consulta por pagador não configurado. Acesse Configurações → APIs e Webhooks.');
                    return;
                }

                const payload: Record<string, any> = { numeroCpfCnpj: cpfLimpo };
                if (dataInicio) payload.dataInicio = dataInicio;
                if (dataFim) payload.dataFim = dataFim;

                const response = await fetch(webhookUrl, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    let msg = `Erro ${response.status} ao consultar o Sicoob.`;
                    try { const e = await response.json(); msg = e?.mensagem || e?.message || e?.error || msg; } catch (_) {}
                    throw new Error(msg);
                }

                const data = await response.json();
                const lista: SicoobBoleto[] = Array.isArray(data?.resultado) ? data.resultado
                    : Array.isArray(data) ? data
                    : [];

                if (lista.length === 0) {
                    setErro('Nenhum boleto encontrado para este CPF/CNPJ no período informado.');
                    return;
                }

                setResultadoLista(lista);
            }
        } catch (err: any) {
            setErro(err.message || 'Erro inesperado ao consultar o Sicoob.');
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSearch();
    };

    const handleReset = () => {
        setResultado(null);
        setResultadoLista([]);
        setAmbiguo(false);
        setErro(null);
        setNossoNumero('');
        setLinhaDigitavel('');
        setCpfCnpj('');
        setDataInicio('');
        setDataFim('');
        setExpandedIdx(null);
    };

    const formatDate = (val?: string) => {
        if (!val) return null;
        const d = val.split('T')[0].split('-');
        if (d.length === 3) return `${d[2]}/${d[1]}/${d[0]}`;
        return val;
    };

    const formatMoney = (val?: number) => {
        if (val === undefined || val === null) return '—';
        return val.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const getLastSituacao = (boleto: SicoobBoleto): string => {
        // situacaoBoleto é o campo real da API Sicoob
        if (boleto.situacaoBoleto) return boleto.situacaoBoleto;
        if (boleto.situacao) return boleto.situacao;
        if (boleto.listaHistorico && boleto.listaHistorico.length > 0) {
            const last = boleto.listaHistorico[boleto.listaHistorico.length - 1];
            const key = String(last.tipoHistorico ?? '');
            return tipoHistoricoLabel[key] || `Tipo ${last.tipoHistorico}`;
        }
        return 'Em aberto';
    };

    const getPaymentDate = (boleto: SicoobBoleto): string | undefined => {
        const directDate = boleto.dataLiquidacao || boleto.dataMovimentoLiquidacao || boleto.dataPagamento || boleto.dataBaixa || boleto.dataCredito;
        if (directDate) return formatDate(directDate)!;

        if (boleto.listaHistorico && boleto.listaHistorico.length > 0) {
            // Busca de trás pra frente pelo evento de liquidação
            const reversedHistory = [...boleto.listaHistorico].reverse();
            const liquidacao = reversedHistory.find(h => {
                const tipo = String(h.tipoHistorico);
                const desc = (h.descricaoHistorico || h.descricao || '').toLowerCase();
                return tipo === '6' || tipo === '2' || tipo === '3' || desc.includes('liquid') || desc.includes('baixa') || desc.includes('pago');
            });
            if (liquidacao) {
                return formatDate(liquidacao.dataHistorico || liquidacao.data);
            }
            
            // Se o status principal já é liquidado mas não achamos o evento, usamos a data da última movimentação
            const s = (boleto.situacaoBoleto || boleto.situacao || '').toLowerCase();
            if (s.includes('liquid') || s.includes('pago') || s.includes('baixa')) {
                 const last = reversedHistory[0]; // já está invertido
                 return formatDate(last.dataHistorico || last.data);
            }
        }
        return undefined;
    };

    const getSituacaoColor = (situacao: string): string => {
        const s = situacao.toLowerCase();
        if (s.includes('liquid') || s.includes('pago') || s.includes('baixa')) {
            return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400';
        }
        if (s.includes('protesto') || s.includes('cancelado')) {
            return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400';
        }
        if (s.includes('em aberto') || s.includes('altera') || s.includes('prorroga')) {
            return 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400';
        }
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300';
    };

    const hasResults = resultado !== null || resultadoLista.length > 0;
    const canSearch = searchMode === 'individual'
        ? (nossoNumero.trim() || linhaDigitavel.trim())
        : cpfCnpj.trim();

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in p-4" {...propsFundoModal(() => onClose())}>
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col border border-gray-100 dark:border-gray-700 animate-scale-in overflow-hidden" onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-700 shrink-0">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary-light rounded-lg">
                            <Search size={20} />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-gray-900 dark:text-white">Consultar Cobrança Sicoob</h2>
                            <p className="text-xs text-gray-500 dark:text-gray-400">Consulte por Nosso Número, Linha Digitável ou CPF/CNPJ do pagador</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-5">

                    {/* Mode Tabs */}
                    {!hasResults && (
                        <div className="flex gap-1 p-1 bg-gray-100 dark:bg-gray-700/50 rounded-xl">
                            <button
                                onClick={() => { setSearchMode('individual'); setErro(null); }}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${searchMode === 'individual' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                            >
                                <Hash size={14} />
                                Por Boleto
                            </button>
                            <button
                                onClick={() => { setSearchMode('cpf'); setErro(null); }}
                                className={`flex-1 flex items-center justify-center gap-2 py-2 px-3 rounded-lg text-sm font-semibold transition-all ${searchMode === 'cpf' ? 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm' : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'}`}
                            >
                                <User size={14} />
                                Por CPF/CNPJ
                            </button>
                        </div>
                    )}

                    {/* Search Fields — Individual */}
                    {!hasResults && searchMode === 'individual' && (
                        <div className="space-y-3">
                            <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-widest">
                                Informe ao menos um critério
                            </p>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nosso Número</label>
                                <div className="relative">
                                    <Hash size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="number"
                                        value={nossoNumero}
                                        onChange={(e) => setNossoNumero(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Ex: 1736"
                                        className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-sm"
                                    />
                                </div>
                            </div>

                            <div className="flex items-center gap-3">
                                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                                <span className="text-xs font-semibold text-gray-400 uppercase">ou</span>
                                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Linha Digitável</label>
                                <div className="relative">
                                    <CreditCard size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={linhaDigitavel}
                                        onChange={(e) => setLinhaDigitavel(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="47 dígitos"
                                        className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-sm font-mono"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Search Fields — CPF */}
                    {!hasResults && searchMode === 'cpf' && (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">CPF / CNPJ do Pagador <span className="text-red-400">*</span></label>
                                <div className="relative">
                                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input
                                        type="text"
                                        value={cpfCnpj}
                                        onChange={(e) => setCpfCnpj(e.target.value)}
                                        onKeyDown={handleKeyDown}
                                        placeholder="Somente números"
                                        className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-sm font-mono"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data início</label>
                                    <div className="relative">
                                        <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="date"
                                            value={dataInicio}
                                            onChange={(e) => setDataInicio(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-sm"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Data fim</label>
                                    <div className="relative">
                                        <Calendar size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="date"
                                            value={dataFim}
                                            onChange={(e) => setDataFim(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all text-sm"
                                        />
                                    </div>
                                </div>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500">As datas são opcionais. Sem elas, retorna todos os boletos do pagador.</p>
                        </div>
                    )}

                    {/* Error */}
                    {erro && (
                        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl flex items-start gap-3">
                            <AlertCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                            <div>
                                <p className="text-sm font-semibold text-red-700 dark:text-red-400">Erro na consulta</p>
                                <p className="text-sm text-red-600 dark:text-red-300 mt-0.5">{erro}</p>
                            </div>
                        </div>
                    )}

                    {/* Result — Individual Boleto */}
                    {resultado && (
                        <div className="space-y-4 animate-fade-in">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={18} className="text-green-500" />
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">Boleto encontrado</span>
                                    {resultado.conta_nome && (
                                        <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${COR_ETIQUETA_CONTA[resultado.conta ?? ''] ?? COR_ETIQUETA_OUTRA}`}>
                                            {resultado.conta_nome}
                                        </span>
                                    )}
                                </div>
                                <span className={`px-3 py-1 rounded-full text-xs font-bold ${getSituacaoColor(getLastSituacao(resultado))}`}>
                                    {getLastSituacao(resultado)}
                                </span>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <DollarSign size={14} className="text-primary dark:text-primary-light" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Valor</span>
                                    </div>
                                    <p className="text-xl font-bold text-gray-900 dark:text-white">{formatMoney(resultado.valor)}</p>
                                    {resultado.valorAbatimento !== undefined && resultado.valorAbatimento !== null && resultado.valorAbatimento > 0 && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Abatimento: {formatMoney(resultado.valorAbatimento)}</p>
                                    )}
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Hash size={14} className="text-primary dark:text-primary-light" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Nosso Número</span>
                                    </div>
                                    <p className="text-lg font-bold text-gray-900 dark:text-white font-mono">{resultado.nossoNumero ?? '—'}</p>
                                    {resultado.seuNumero && (
                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Seu N°: {resultado.seuNumero}</p>
                                    )}
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Calendar size={14} className="text-primary dark:text-primary-light" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Emissão</span>
                                    </div>
                                    <p className="text-base font-bold text-gray-900 dark:text-white">{formatDate(resultado.dataEmissao) ?? '—'}</p>
                                </div>

                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Calendar size={14} className="text-red-500" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Vencimento</span>
                                    </div>
                                    <p className="text-base font-bold text-gray-900 dark:text-white">{formatDate(resultado.dataVencimento) ?? '—'}</p>
                                    {getPaymentDate(resultado) && (
                                        <p className="text-xs text-green-600 dark:text-green-500 mt-0.5 font-semibold">Pago: {getPaymentDate(resultado)}</p>
                                    )}
                                </div>
                            </div>

                            {resultado.pagador?.nome && (
                                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-2">
                                    <div className="flex items-center gap-2">
                                        <User size={14} className="text-primary dark:text-primary-light" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Pagador</span>
                                    </div>
                                    <p className="text-sm font-bold text-gray-900 dark:text-white">{resultado.pagador.nome}</p>
                                    {resultado.pagador.numeroCpfCnpj && (
                                        <p className="text-xs text-gray-500 dark:text-gray-400">CPF/CNPJ: {resultado.pagador.numeroCpfCnpj}</p>
                                    )}
                                    {resultado.pagador.endereco && (
                                        <div className="flex items-start gap-1.5">
                                            <MapPin size={12} className="text-gray-400 shrink-0 mt-0.5" />
                                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                                {resultado.pagador.endereco}
                                                {resultado.pagador.bairro && `, ${resultado.pagador.bairro}`}
                                                {resultado.pagador.cidade && ` — ${resultado.pagador.cidade}`}
                                                {resultado.pagador.uf && `/${resultado.pagador.uf}`}
                                                {resultado.pagador.cep && ` — CEP ${resultado.pagador.cep}`}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {resultado.linhaDigitavel && (
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block">Linha Digitável</span>
                                    <div className="flex items-center gap-2">
                                        <input type="text" readOnly value={resultado.linhaDigitavel}
                                            className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none" />
                                        <button onClick={() => handleCopy(resultado.linhaDigitavel!, 'Linha Digitável')}
                                            className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300">
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {resultado.codigoBarras && (
                                <div className="space-y-1.5">
                                    <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest block">Código de Barras</span>
                                    <div className="flex items-center gap-2">
                                        <input type="text" readOnly value={resultado.codigoBarras}
                                            className="flex-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2.5 text-sm text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none" />
                                        <button onClick={() => handleCopy(resultado.codigoBarras!, 'Código de Barras')}
                                            className="flex shrink-0 items-center justify-center p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300">
                                            <Copy size={16} />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {resultado.listaHistorico && resultado.listaHistorico.length > 0 && (
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2">
                                        <Info size={14} className="text-gray-400" />
                                        <span className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest">Histórico de Movimentações</span>
                                    </div>
                                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                                        {[...resultado.listaHistorico].reverse().map((h, i) => {
                                            const key = String(h.tipoHistorico ?? '');
                                            const label = tipoHistoricoLabel[key] || `Tipo ${h.tipoHistorico}`;
                                            const date = formatDate(h.dataHistorico || h.data);
                                            const desc = h.descricaoHistorico || h.descricao;
                                            return (
                                                <div key={i} className="flex flex-col gap-1 p-2.5 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold shrink-0 ${getSituacaoColor(label)}`}>
                                                            {label}
                                                        </span>
                                                        {date && (
                                                            <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                                                                <Calendar size={10} />{date}
                                                            </span>
                                                        )}
                                                    </div>
                                                    {desc && (
                                                        <p className="text-xs text-gray-600 dark:text-gray-300 leading-relaxed pl-1">{desc}</p>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Result — CPF List */}
                    {resultadoLista.length > 0 && (
                        <div className="space-y-3 animate-fade-in">
                            {ambiguo && (
                                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-800 rounded-xl flex items-start gap-3">
                                    <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                                    <div>
                                        <p className="text-sm font-bold text-amber-800 dark:text-amber-300">
                                            Este número existe em mais de uma conta
                                        </p>
                                        <p className="text-sm text-amber-700 dark:text-amber-300/90 mt-0.5">
                                            Cada conta Sicoob da Vigor numera os boletos separadamente, então o mesmo
                                            Nosso Número pode ser de <strong>clientes diferentes</strong> em cada uma.
                                            Confira o nome do pagador e o valor antes de agir sobre qualquer um deles.
                                        </p>
                                    </div>
                                </div>
                            )}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <CheckCircle2 size={18} className="text-green-500" />
                                    <span className="text-sm font-bold text-gray-700 dark:text-gray-200">
                                        {resultadoLista.length} boleto{resultadoLista.length > 1 ? 's' : ''} encontrado{resultadoLista.length > 1 ? 's' : ''}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
                                {resultadoLista.map((b, idx) => (
                                    <div key={idx} className="border border-gray-100 dark:border-gray-700 rounded-xl overflow-hidden">
                                        <button
                                            onClick={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                                            className="w-full flex items-center justify-between p-3.5 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                                        >
                                            <div className="flex items-center gap-3 min-w-0">
                                                <FileText size={14} className="text-gray-400 shrink-0" />
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-sm font-bold text-gray-900 dark:text-white">{formatMoney(b.valor)}</span>
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${getSituacaoColor(getLastSituacao(b))}`}>
                                                            {getLastSituacao(b)}
                                                        </span>
                                                        {b.conta_nome && (
                                                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${COR_ETIQUETA_CONTA[b.conta ?? ''] ?? COR_ETIQUETA_OUTRA}`}>
                                                                {b.conta_nome}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                        N° {b.nossoNumero ?? '—'} • Venc. {formatDate(b.dataVencimento) ?? '—'}
                                                        {b.pagador?.nome && ` • ${b.pagador.nome}`}
                                                    </p>
                                                </div>
                                            </div>
                                            {expandedIdx === idx ? <ChevronUp size={16} className="text-gray-400 shrink-0" /> : <ChevronDown size={16} className="text-gray-400 shrink-0" />}
                                        </button>

                                        {expandedIdx === idx && (
                                            <div className="p-3.5 border-t border-gray-100 dark:border-gray-700 space-y-2.5 bg-white dark:bg-gray-800">
                                                <div className="text-xs">
                                                    <div>
                                                        <span className="text-gray-400 dark:text-gray-500 uppercase font-bold tracking-wide">Emissão</span>
                                                        <p className="font-semibold text-gray-800 dark:text-gray-200 mt-0.5">{formatDate(b.dataEmissao) ?? '—'}</p>
                                                    </div>
                                                </div>
                                                {b.linhaDigitavel && (
                                                    <div className="flex items-center gap-2">
                                                        <input type="text" readOnly value={b.linhaDigitavel}
                                                            className="flex-1 bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-2 text-xs text-gray-700 dark:text-gray-300 font-mono truncate focus:outline-none" />
                                                        <button onClick={() => handleCopy(b.linhaDigitavel!, 'Linha Digitável')}
                                                            className="flex shrink-0 items-center justify-center p-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors text-gray-600 dark:text-gray-300">
                                                            <Copy size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-100 dark:border-gray-700 shrink-0 flex justify-between items-center gap-3">
                    {hasResults ? (
                        <>
                            <button onClick={handleReset}
                                className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2">
                                <Search size={16} />
                                Nova consulta
                            </button>
                            <button onClick={onClose}
                                className="px-5 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                                Fechar
                            </button>
                        </>
                    ) : (
                        <>
                            <button onClick={onClose}
                                className="px-5 py-2 text-sm font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSearch}
                                disabled={loading || !canSearch}
                                className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {loading ? (
                                    <><Loader2 size={16} className="animate-spin" />Consultando...</>
                                ) : (
                                    <><Search size={16} />Consultar</>
                                )}
                            </button>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ConsultarCobrancaModal;
